<script lang="ts" setup>
import { ref, computed } from 'vue';
import { formatDate } from '@/utils/date-utils';
import { useMutation } from '@vue/apollo-composable';
import { ApiKeyUpdateDocument, ApiKeyType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BBadge,
  BCard,
  BFormCheckbox,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';

// --- Props for user details ---
const props = defineProps<{
  apiKeys: (ApiKeyType | null)[];
  loading: boolean;
}>();

// --- Emit when access token revoked ---
const emits = defineEmits(['apiKeyRevoked']);

// --- Local state for API token update ---
const updatingToken = ref(false);
const updateTokenError = ref<string | null>(null);
const updateTokenSuccess = ref(false);
const updateTokenShowSuccessIndicator = ref(false);

// --- Mutation to update API token ---
const {
  mutate: updateApiKey,
  onDone: onUpdateTokenDone,
  onError: onUpdateTokenError,
} = useMutation(ApiKeyUpdateDocument);

// When the create mutation returns, show a success message and refetch tokens.
onUpdateTokenDone((res) => {
  console.debug('Token update done, response:', res);
  if (res.data && res.data.apiKeyUpdate) {
    updateTokenError.value = null;
    updateTokenShowSuccessIndicator.value = true;
    updateTokenSuccess.value = true;

    setTimeout(() => {
      updateTokenShowSuccessIndicator.value = false;
      showApiKeyRevokeModal.value = false;
    }, 2000);

    emits('apiKeyRevoked'); // emit to refresh active access tokens upstream
  } else {
    console.error('Token update failed, missing token data:', res.data);
    updateTokenSuccess.value = false;
    updateTokenShowSuccessIndicator.value = false;
    updateTokenError.value =
      'Token update failed, response is missing the token data: ' + res.data;
  }
  updatingToken.value = false;
  apiKeyIdToRemove.value = null;
});

// Handle any errors during token creation.
onUpdateTokenError((err) => {
  console.error('Token update error:', err);
  updateTokenError.value = err.message;
  updateTokenSuccess.value = false;
  updateTokenShowSuccessIndicator.value = false;
  updatingToken.value = false;
});

// Function to handle token creation form submission.
function handleRevokeToken(id: number) {
  // Clear previous messages
  updateTokenError.value = null;
  updateTokenSuccess.value = false;
  updateTokenShowSuccessIndicator.value = false;
  updatingToken.value = true;

  updateApiKey({
    id: id,
    revokedAt: new Date(Date.now()),
  });
}

// --- List of keys ---
const isKeyInactive = (key: ApiKeyType | null): boolean => {
  if (!key) return true;
  return !!key.revokedAt || (!!key.expiresAt && key.expiresAt < new Date());
};

const showInactiveApiKeys = ref(false);

const filteredApiKeys = computed(() => {
  if (!props.apiKeys) return [];

  if (showInactiveApiKeys.value) {
    // Show all keys, including revoked ones
    return props.apiKeys.map((key) => ({
      ...key, // Spread original key properties
      _rowVariant: getRowVariant(key), // Assign row variant dynamically
    }));
  } else {
    // Hide revoked keys
    return props.apiKeys.filter((key) => !isKeyInactive(key));
  }
});

const getRowVariant = (item: ApiKeyType | null): string => {
  if (!item) return '';
  if (isKeyInactive(item)) {
    return 'danger';
  }
  return 'success';
};

// --- API Key Table fields (hide revokedAt if showInactiveApiKeys is false) ---
const apiKyesTablefields = computed(() => {
  const fields = [
    {
      key: 'keyName',
      label: 'Name',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'permissions',
      label: 'Permissions',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    {
      key: 'expiresAt',
      label: 'Expiration',
      sortable: true,
      formatter: (v: Date | string | undefined | null) => formatDate(v, '-'),
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  if (showInactiveApiKeys.value) {
    fields.splice(5, 0, {
      key: 'revokedAt',
      label: 'Revoked',
      sortable: true,
      formatter: (v) => formatDate(v, '-'),
      tdClass: 'align-middle',
    });
  }

  return fields;
});

// --- Refs to handle the API key revoke modal
const showApiKeyRevokeModal = ref(false);
const apiKeyIdToRemove = ref<number | null>(null);

function revokeApiKeyButtonClicked(id: number) {
  console.debug('Revoke button clicked for key:', id);
  apiKeyIdToRemove.value = id;
  showApiKeyRevokeModal.value = true;
}

function revokeApiKeyConfirmed(event: Event) {
  // Prevent the modal from closing
  event.preventDefault();

  // This happens when clicked modal button multiple times while update is in progress
  if (apiKeyIdToRemove.value == null) {
    console.error('No API key ID to revoke');
    return;
  }

  // Call the mutation to revoke the token
  handleRevokeToken(apiKeyIdToRemove.value);
}

function apiKeyVariant(permissions: string): string {
  if (permissions.includes('admin')) {
    return 'primary';
  } else if (permissions.includes('write')) {
    return 'success';
  } else if (permissions.includes('read')) {
    return 'success';
  } else if (permissions.includes('agent')) {
    return 'warning';
  }
  return 'secondary';
}

function apiKeyIcon(permissions: string): string {
  if (permissions.includes('admin')) {
    return 'fas fa-shield-halved';
  } else if (permissions.includes('write')) {
    return 'fas fa-pencil-alt';
  } else if (permissions.includes('read')) {
    return 'fas fa-eye';
  } else if (permissions.includes('agent')) {
    return 'fas fa-user-secret';
  }
  return 'fas fa-question';
}
</script>

<template>
  <div>
    <div v-if="!filteredApiKeys.length">
      <h5 class="my-3 py-3 text-center">
        You have no personal access tokens to show.
      </h5>
    </div>
    <div v-else>
      <BCard>
        <b-table
          :items="filteredApiKeys"
          :busy="props.loading"
          :fields="apiKyesTablefields"
          small
          striped
          responsive
          hover
          class="browser-table"
        >
          <template #cell(id)="data">
            <b-button
              v-if="!isKeyInactive(data.item)"
              variant="outline-danger"
              size="sm"
              @click="revokeApiKeyButtonClicked(data.value)"
            >
              Revoke
            </b-button>
          </template>
          <template #cell(permissions)="data">
            <b-badge
              v-for="perm in data.value.split(';')"
              :key="perm"
              :variant="apiKeyVariant(perm)"
              class="me-1"
            >
              <font-awesome-icon :icon="apiKeyIcon(perm)" class="me-1" />
              {{ perm }}
            </b-badge>
          </template>
        </b-table>
      </BCard>
    </div>
    <b-form-checkbox v-model="showInactiveApiKeys" name="check-button" switch>
      show inactive access tokens
    </b-form-checkbox>

    <!-- Confirmation Modal -->
    <b-modal
      v-if="apiKeyIdToRemove"
      title="Confirm action"
      ok-title="Confirm"
      cancel-title="Cancel"
      v-model="showApiKeyRevokeModal"
      @ok="revokeApiKeyConfirmed"
      :ok-disabled="updatingToken"
    >
      <p v-if="updatingToken">
        Saving Changes
        <b-spinner small label=""></b-spinner>
      </p>
      <p v-else-if="updateTokenShowSuccessIndicator">
        Changes Saved
        <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
      </p>
      <p v-else>Are you sure you want to revoke the access token?</p>
      <b-alert v-model="updateTokenError" variant="danger" class="mt-3">
        {{ updateTokenError }}
      </b-alert>
    </b-modal>
  </div>
</template>
<style scoped></style>
